import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/layout/Navbar'
import { Button } from '../components/ui/Button'
import { useOnboardingContext } from '../contexts/OnboardingContext'
import { FlowType, FlowVariant } from '../types/onboarding.types'
import { authService } from '../services/authService'
import toast from 'react-hot-toast'

export const FlowSelectionPage = () => {
  const navigate = useNavigate()
  const { initializeFlow, refreshProgress } = useOnboardingContext()
  const [selectedFlow, setSelectedFlow] = useState<FlowVariant | null>(null)
  const [isInitializing, setIsInitializing] = useState(false)
  const [isAutoProcessing, setIsAutoProcessing] = useState(false)

  // Debug: Log state changes
  console.log(
    '🐛 FlowSelectionPage render - selectedFlow:',
    selectedFlow,
    'isInitializing:',
    isInitializing,
    'isAutoProcessing:',
    isAutoProcessing
  )

  // Detectar selección automática desde pending-proposals
  useEffect(() => {
    const autoSelectedFlow = localStorage.getItem('autoSelectedFlow')

    if (autoSelectedFlow) {
      console.log('🔄 Auto-selected flow detected:', autoSelectedFlow)

      // Activar modo de auto-procesamiento para mostrar pantalla de carga
      setIsAutoProcessing(true)

      // Limpiar el localStorage para evitar loops
      localStorage.removeItem('autoSelectedFlow')

      // Mapear string a FlowVariant
      let flowVariant: FlowVariant
      if (autoSelectedFlow === 'persona_moral') {
        flowVariant = FlowVariant.PERSONA_MORAL
      } else if (autoSelectedFlow === 'persona_fisica') {
        flowVariant = FlowVariant.PERSONA_FISICA
      } else {
        console.warn('⚠️ Unknown auto-selected flow:', autoSelectedFlow)
        setIsAutoProcessing(false)
        return
      }

      console.log('🎯 Setting selected flow automatically:', flowVariant)
      setSelectedFlow(flowVariant)

      // Ejecutar automáticamente la lógica de continuar después de un pequeño delay
      // para asegurar que el estado se actualice
      setTimeout(() => {
        console.log('🚀 Auto-triggering handleContinue...')
        handleContinueInternal(flowVariant)
      }, 100)
    }
  }, []) // Función interna para evitar dependencias del useEffect
  const handleContinueInternal = async (flowVariant: FlowVariant) => {
    console.log('🚀 HandleContinueInternal called, selectedFlow:', flowVariant)

    console.log('✅ Starting initialization...')
    setIsInitializing(true)

    try {
      console.log(
        '🔄 Calling initializeFlow with:',
        FlowType.LEASING,
        flowVariant
      )
      // Initialize the onboarding flow
      await initializeFlow(FlowType.LEASING, flowVariant)

      console.log('✅ Flow initialized successfully')

      // CRITICAL: Refresh progress to ensure context has latest data
      console.log('🔄 Refreshing progress to sync context...')
      await refreshProgress()

      console.log(
        '✅ Progress refreshed, now navigating to application-start...'
      )

      // Navegar a application-start que es común para ambos flujos
      toast.success('Flujo inicializado correctamente')

      // Determinar la nueva URL de progreso
      let newProgressURL: string
      if (flowVariant === FlowVariant.PERSONA_MORAL) {
        newProgressURL = '/onboarding/application-start?flow=persona_moral'
        console.log('🏢 Navigating to application-start for Persona Moral')
      } else {
        newProgressURL = '/onboarding/application-start?flow=persona_fisica'
        console.log('👤 Navigating to application-start for Persona Física')
      }

      // Actualizar progressURL en el backend antes de navegar
      try {
        console.log('🔄 Updating progress URL in backend...')
        const creditAppId = await authService.getCurrentCreditApplicationId()

        if (creditAppId) {
          const updateSuccess =
            await authService.updateCreditApplicationProgress(
              creditAppId,
              newProgressURL
            )

          if (updateSuccess) {
            console.log('✅ Progress URL updated successfully in backend')
          } else {
            console.warn(
              '⚠️ Failed to update progress URL in backend, but continuing...'
            )
          }
        } else {
          console.warn(
            '⚠️ No credit application ID found, skipping progress update'
          )
        }
      } catch (progressError) {
        console.error('❌ Error updating progress URL:', progressError)
        // No detener el flujo por este error, solo loggearlo
      }

      // Navegar a la nueva URL
      navigate(newProgressURL)
    } catch (error) {
      console.error('❌ Error initializing flow:', error)
      toast.error(
        'Error al inicializar el flujo. Por favor intenta nuevamente.'
      )
    } finally {
      console.log('🏁 Setting isInitializing to false')
      setIsInitializing(false)
    }
  }

  const flowOptions = [
    {
      type: FlowVariant.PERSONA_MORAL,
      title: 'Persona Moral',
      subtitle: 'Empresa o Sociedad',
      description:
        'Ideal para empresas constituidas, sociedades anónimas, de responsabilidad limitada u otros tipos de personas morales.',
      features: [
        'Verificación de datos empresariales',
        'Consulta CIEC y SAT',
        'Validación de representante legal',
        'Documentación corporativa',
        'Buró de crédito empresarial y personal',
      ],
      steps: '12 pasos',
      estimatedTime: '45-60 minutos',
      icon: '🏢',
      bgColor: 'from-blue-500 to-blue-600',
      borderColor: 'border-blue-200',
      hoverColor: 'hover:border-blue-400',
    },
    {
      type: FlowVariant.PERSONA_FISICA,
      title: 'Persona Física',
      subtitle: 'Individual',
      description:
        'Perfecto para personas físicas con actividad empresarial o profesionales independientes.',
      features: [
        'Verificación CIEC y SAT personal',
        'Validación de identidad e ingresos',
        'Documentación personal completa',
        'Referencias personales',
        'Buró de crédito individual',
      ],
      steps: '11 pasos',
      estimatedTime: '40-55 minutos',
      icon: '👤',
      bgColor: 'from-green-500 to-green-600',
      borderColor: 'border-green-200',
      hoverColor: 'hover:border-green-400',
    },
  ]

  const handleFlowSelection = (flowType: FlowVariant) => {
    console.log('🎯 Flow selected:', flowType)
    setSelectedFlow(flowType)
  }

  const handleContinue = async () => {
    console.log('🚀 HandleContinue called, selectedFlow:', selectedFlow)

    if (!selectedFlow) {
      console.log('❌ No selectedFlow, showing error')
      toast.error('Por favor selecciona un tipo de flujo')
      return
    }

    // Usar la función interna que contiene toda la lógica
    await handleContinueInternal(selectedFlow)
  }

  const handleCancel = () => {
    navigate('/dashboard')
  }

  return (
    <>
      {/* Pantalla de carga cuando se está procesando automáticamente */}
      {isAutoProcessing && (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
          <div className="text-center">
            {/* Logo */}
            <div className="mb-8">
              <img
                src="/assets/olr-logo.png"
                alt="OLR Leasing"
                className="w-32 h-auto mx-auto"
              />
            </div>

            {/* Spinner de carga */}
            <div className="mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            </div>

            {/* Mensaje de carga */}
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-gray-900">
                Inicializando tu proceso
              </h2>
              <p className="text-gray-600 max-w-md mx-auto">
                Estamos preparando todo para comenzar con tu solicitud de
                arrendamiento...
              </p>
            </div>

            {/* Indicador de progreso opcional */}
            <div className="mt-8">
              <div className="w-64 bg-gray-200 rounded-full h-2 mx-auto">
                <div
                  className="bg-blue-600 h-2 rounded-full animate-pulse"
                  style={{ width: '60%' }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UI normal solo cuando NO está en modo auto-processing */}
      {!isAutoProcessing && (
        <>
          {/* Mobile Layout */}
          <div className="min-h-screen flex flex-col md:hidden">
            <div className="h-24 flex items-center justify-center relative bg-gradient-to-b from-olr-gradient-start via-olr-gradient-middle to-olr-gradient-end">
              <img
                src="/assets/olr-logo.png"
                alt="OLR Leasing"
                className="filter brightness-0 invert w-24 h-auto"
              />
              <div className="absolute right-4">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
            </div>

            <div className="flex-1 bg-olr-white p-6">
              <div className="text-center mb-8">
                <h1 className="text-lg sm:text-h3 text-olr-dark mb-3 sm:mb-4 font-semibold">
                  Selecciona tu tipo de persona
                </h1>
                <p className="text-sm sm:text-paragraph-2 text-olr-body leading-relaxed">
                  Elige el tipo de persona que mejor describa tu situación para
                  personalizar el proceso de arrendamiento según tus necesidades
                  específicas.
                </p>
              </div>

              <div className="space-y-4">
                {flowOptions.map(option => (
                  <div
                    key={option.type}
                    className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                      selectedFlow === option.type
                        ? `${option.borderColor} bg-gradient-to-br ${option.bgColor} bg-opacity-5`
                        : `border-gray-200 ${option.hoverColor} hover:shadow-sm`
                    }`}
                    onClick={() => handleFlowSelection(option.type)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div
                          className={`w-12 h-12 rounded-full bg-gradient-to-br ${option.bgColor} flex items-center justify-center text-white text-xl`}
                        >
                          {option.icon}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-base font-semibold text-olr-dark">
                            {option.title}
                          </h3>
                          <span className="text-sm text-gray-500">
                            ({option.subtitle})
                          </span>
                        </div>

                        <p className="text-sm text-olr-body mb-3">
                          {option.description}
                        </p>

                        <div className="space-y-2 mb-3">
                          {option.features.slice(0, 3).map((feature, index) => (
                            <div
                              key={index}
                              className="flex items-center space-x-2"
                            >
                              <svg
                                className="w-4 h-4 text-green-500 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              <span className="text-xs text-gray-600">
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{option.steps}</span>
                          <span>{option.estimatedTime}</span>
                        </div>
                      </div>

                      {selectedFlow === option.type && (
                        <div className="flex-shrink-0">
                          <svg
                            className="w-6 h-6 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col space-y-3 pt-8">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleContinue}
                  className="w-full"
                  disabled={!selectedFlow || isInitializing}
                >
                  {isInitializing ? 'Inicializando...' : 'Comenzar proceso'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                  className="w-full"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex min-h-screen flex-col">
            <Navbar />
            <div
              className="flex-1 flex items-center justify-center py-12"
              style={{
                backgroundImage: 'url(/assets/bg-flow.svg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            >
              <div className="max-w-6xl w-full mx-8 px-16 py-12">
                <div className="text-center mb-12">
                  <h1 className="text-h2 text-olr-dark mb-6">
                    Selecciona tu tipo de persona
                  </h1>
                  <p className="text-paragraph-1 text-olr-body max-w-4xl mx-auto">
                    Elige el tipo de persona que mejor describa tu situación
                    para personalizar el proceso de arrendamiento según tus
                    necesidades específicas.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  {flowOptions.map(option => (
                    <div
                      key={option.type}
                      className={`relative p-8 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                        selectedFlow === option.type
                          ? `${option.borderColor} bg-gradient-to-br ${option.bgColor} bg-opacity-5 shadow-lg`
                          : `border-gray-200 ${option.hoverColor} hover:shadow-md`
                      }`}
                      onClick={() => handleFlowSelection(option.type)}
                    >
                      <div className="text-center mb-6">
                        <div
                          className={`w-20 h-20 rounded-full bg-gradient-to-br ${option.bgColor} flex items-center justify-center text-white text-3xl mx-auto mb-4`}
                        >
                          {option.icon}
                        </div>
                        <h3 className="text-h4 font-bold text-olr-dark mb-2">
                          {option.title}
                        </h3>
                        <p className="text-paragraph-2 text-gray-500 mb-4">
                          {option.subtitle}
                        </p>
                        <p className="text-paragraph-2 text-olr-body">
                          {option.description}
                        </p>
                      </div>

                      <div className="space-y-3 mb-6">
                        {option.features.map((feature, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-3"
                          >
                            <svg
                              className="w-5 h-5 text-green-500 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="text-base text-gray-700">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="border-t pt-4 flex items-center justify-between text-sm text-gray-500">
                        <span className="font-medium">{option.steps}</span>
                        <span>{option.estimatedTime}</span>
                      </div>

                      {selectedFlow === option.type && (
                        <div className="absolute top-4 right-4">
                          <svg
                            className="w-8 h-8 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex justify-center space-x-6">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCancel}
                    className="px-8"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleContinue}
                    disabled={!selectedFlow || isInitializing}
                    className="px-8"
                  >
                    {isInitializing ? 'Inicializando...' : 'Comenzar proceso'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
